/**
 * Studio error taxonomy (AI-7467).
 *
 * Maps backend `error_code` values from the `/studio/ask` NDJSON stream and
 * HTTP error bodies to contextual, actionable, user-facing messages.
 *
 * Discriminator style mirrors `apps/web/src/helpers/handleSaveError.ts`.
 */

export const STUDIO_INTERNAL_ERROR_CODE = "studio.internal.error";

/**
 * Error carrier for Studio failures (AI-7467).
 *
 * The AI SDK `error` UIMessageChunk only carries a string, so structured
 * fields (`code`, `retryable`, `status`) cannot ride on the chunk. Instead the
 * transport surfaces stream errors via `controller.error(new StudioStreamError())`
 * and HTTP errors by throwing this from `sendMessages`. Both flow to the AI SDK's
 * `onError` with object identity preserved, so `useAgentChat` can read the code.
 */
export class StudioStreamError extends Error {
  code?: string;
  retryable?: boolean;
  status?: number;

  constructor(message: string, options?: { code?: string; retryable?: boolean; status?: number }) {
    super(message);
    // Restore the prototype chain so `instanceof StudioStreamError` holds even
    // if the class is downleveled (extending native Error breaks it on ES5).
    Object.setPrototypeOf(this, StudioStreamError.prototype);
    this.name = "StudioStreamError";
    this.code = options?.code;
    this.retryable = options?.retryable;
    this.status = options?.status;
  }
}

interface StudioErrorEntry {
  title: string;
  description: string;
}

/**
 * User-facing message per backend `error_code`. Keys mirror the backend
 * taxonomy exactly (AI-7467 comment 50347).
 */
export const STUDIO_ERROR_MESSAGES: Record<string, StudioErrorEntry> = {
  "studio.model.rate_limit": {
    title: "AI service busy",
    description: "The AI service is busy right now. Please retry in a moment.",
  },
  "studio.model.overloaded": {
    title: "AI service overloaded",
    description: "The AI service is temporarily overloaded. Please try again shortly.",
  },
  "studio.model.server_error": {
    title: "AI service error",
    description: "The AI service hit an error. Please try again.",
  },
  "studio.model.invalid_request": {
    title: "Invalid request",
    description: "We couldn't process this request. Try rephrasing your question.",
  },
  "studio.model.auth_failed": {
    title: "AI service misconfigured",
    description: "The AI service is misconfigured. Our team has been notified.",
  },
  "studio.model.timeout": {
    title: "AI service timed out",
    description: "The AI service didn't respond in time. Please try again.",
  },
  "studio.quota.credits_exhausted": {
    title: "Credit balance exhausted",
    description: "The AI service credit balance is exhausted. Please contact your administrator.",
  },
  "studio.quota.usage_exceeded": {
    title: "Usage limit reached",
    description:
      "You've reached your Studio usage limit. Please contact your administrator or try again later.",
  },
  "studio.tool.execution_failed": {
    title: "Analysis step failed",
    description: "A step in the analysis failed. Please try again.",
  },
  "studio.tool.timeout": {
    title: "Analysis step timed out",
    description: "A step in the analysis timed out. Please try again.",
  },
  "studio.tool.unavailable": {
    title: "Data connection unavailable",
    description: "A required data connection is unavailable right now. Please try again shortly.",
  },
  "studio.agent.step_limit": {
    title: "Analysis step limit reached",
    description:
      "The analysis reached its step limit before it could prepare a response. Please retry or narrow your request.",
  },
  "studio.session.not_found": {
    title: "Session not found",
    description: "This session could not be found.",
  },
  "studio.session.gone": {
    title: "Session unavailable",
    description: "This session has been archived or deleted.",
  },
  "studio.session.not_cancellable": {
    title: "Cannot cancel request",
    description: "This request can no longer be cancelled.",
  },
  "studio.session.init_failed": {
    title: "Couldn't start request",
    description: "We couldn't start your request. Please try again.",
  },
  "studio.auth.feature_disabled": {
    title: "Studio not enabled",
    description: "Studio isn't enabled for your account.",
  },
  "studio.auth.no_team_selected": {
    title: "No team selected",
    description: "Please select a team to continue.",
  },
  "studio.auth.unauthenticated": {
    title: "Session expired",
    description: "Your session has expired. Please sign in again.",
  },
  "studio.auth.forbidden_tenant": {
    title: "Access denied",
    description: "You don't have access to Studio for this account.",
  },
  "studio.input.empty_message": {
    title: "Empty message",
    description: "Please enter a message.",
  },
  [STUDIO_INTERNAL_ERROR_CODE]: {
    title: "Something went wrong",
    description: "Something went wrong. Please try again.",
  },
};

/** Fallback used when no `error_code` or HTTP status maps to a known entry. */
export const DEFAULT_STUDIO_ERROR: StudioErrorEntry =
  STUDIO_ERROR_MESSAGES[STUDIO_INTERNAL_ERROR_CODE];

/**
 * Fallback mapping for failures that carry an HTTP status but no `error_code`
 * (global auth 401/403 have no code in v1).
 */
export const httpStatusToStudioCode = (status: number): string => {
  if (status === 401) {
    return "studio.auth.unauthenticated";
  }
  if (status === 403) {
    return "studio.auth.forbidden_tenant";
  }
  return STUDIO_INTERNAL_ERROR_CODE;
};

export interface ResolveStudioErrorInput {
  code?: string;
  status?: number;
  fallbackMessage?: string;
}

export interface ResolvedStudioError {
  title: string;
  description: string;
  code: string;
}

/**
 * Resolve a backend error to a user-facing title/description and a stable code.
 *
 * Precedence:
 * 1. A known `code` maps directly.
 * 2. Otherwise an HTTP `status` maps via {@link httpStatusToStudioCode}.
 * 3. Otherwise the default error, using `fallbackMessage` as the description
 *    when provided.
 */
export const resolveStudioError = ({
  code,
  status,
  fallbackMessage,
}: ResolveStudioErrorInput): ResolvedStudioError => {
  if (code !== undefined && Object.prototype.hasOwnProperty.call(STUDIO_ERROR_MESSAGES, code)) {
    const directEntry = STUDIO_ERROR_MESSAGES[code];
    return { title: directEntry.title, description: directEntry.description, code };
  }

  if (status !== undefined) {
    const statusCode = httpStatusToStudioCode(status);
    const statusEntry = STUDIO_ERROR_MESSAGES[statusCode];
    if (statusEntry) {
      return {
        title: statusEntry.title,
        description: statusEntry.description,
        code: statusCode,
      };
    }
  }

  const hasFallback = fallbackMessage !== undefined && fallbackMessage.length > 0;
  const description = hasFallback ? fallbackMessage : DEFAULT_STUDIO_ERROR.description;
  return {
    title: DEFAULT_STUDIO_ERROR.title,
    description,
    code: STUDIO_INTERNAL_ERROR_CODE,
  };
};

/** Preserve HTTP conflict/gone details as notices only on the workspace surface. */
export const chatErrorFromException = (error: unknown, workspaceNotices = false) => {
  const studioError = error as Partial<StudioStreamError> | null;
  const message = error instanceof Error ? error.message : "Failed to send message";
  const { code } = resolveStudioError({ code: studioError?.code, status: studioError?.status });
  return {
    message,
    code,
    retryable: studioError?.retryable,
    ...(workspaceNotices && (studioError?.status === 409 || studioError?.status === 410)
      ? { notice: true }
      : {}),
  };
};
